import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import {
  ArrowLeft, RefreshCw, Brain, Volume2, DollarSign, Users,
  TrendingUp, Database, Clock, Zap,
} from 'lucide-react';
import { API_BASE_URL } from 'apiConfig';

const API_BASE = API_BASE_URL;

const SURFACE_LABELS = {
  evidence_brief: 'Evidence Brief',
  ask_article: 'Ask Article',
  compare_studies: 'Compare Studies',
  draft_post: 'Draft Post',
  unknown: 'Other',
};

const COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981'];

function StatCard({ icon: Icon, label, value, sub, color = 'text-indigo-400' }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-2">
          <div className={`p-2 rounded-lg bg-zinc-800 ${color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="text-xs text-zinc-500 uppercase tracking-wide">{label}</span>
        </div>
        <p className="text-2xl font-bold text-zinc-100">{value}</p>
        {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
}

export default function CopilotDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/copilot-dashboard?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        toast.error('Admin access required');
        navigate('/home');
        return;
      }
      if (!res.ok) throw new Error('Failed to load dashboard');
      setData(await res.json());
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [days, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, costs, by_feature, daily_series, top_users, audio_files } = data;

  // Prepare pie data for feature breakdown
  const pieData = Object.entries(by_feature).map(([key, value]) => ({
    name: SURFACE_LABELS[key] || key,
    value,
  }));

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="copilot-dashboard">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost" size="icon"
              onClick={() => navigate('/admin')}
              className="text-zinc-400 hover:text-zinc-100"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Brain className="w-5 h-5 text-indigo-400" />
                AI Usage Dashboard
              </h1>
              <p className="text-xs text-zinc-500">Copilot & Audio analytics</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                variant={days === d ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDays(d)}
                className={days === d
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white text-xs'
                  : 'border-zinc-700 text-zinc-400 hover:text-zinc-100 text-xs'}
                data-testid={`period-${d}d-btn`}
              >
                {d}d
              </Button>
            ))}
            <Button
              variant="ghost" size="icon"
              onClick={fetchData}
              disabled={loading}
              className="text-zinc-400 hover:text-zinc-100"
              data-testid="refresh-dashboard-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-cards">
          <StatCard
            icon={Brain} label="Copilot Calls"
            value={summary.copilot_calls_total}
            sub={`${summary.copilot_calls_24h} last 24h · ${summary.copilot_calls_7d} last 7d`}
            color="text-indigo-400"
          />
          <StatCard
            icon={Volume2} label="Audio Generations"
            value={summary.audio_calls_total}
            sub={`${summary.audio_calls_24h} last 24h · ${summary.audio_calls_7d} last 7d`}
            color="text-cyan-400"
          />
          <StatCard
            icon={DollarSign} label="Est. Cost (USD)"
            value={`$${costs.estimated_total_cost_usd}`}
            sub={`Copilot $${costs.estimated_copilot_cost_usd} · Audio $${costs.estimated_audio_cost_usd}`}
            color="text-amber-400"
          />
          <StatCard
            icon={Users} label="Unique Users"
            value={summary.unique_copilot_users}
            sub={`${summary.cache_entries_active} active cache entries`}
            color="text-emerald-400"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Usage Over Time */}
          <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2" data-testid="usage-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Daily Usage
              </CardTitle>
            </CardHeader>
            <CardContent>
              {daily_series.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={daily_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis
                      dataKey="date" tick={{ fill: '#71717a', fontSize: 11 }}
                      tickFormatter={(v) => v.slice(5)}
                    />
                    <YAxis tick={{ fill: '#71717a', fontSize: 11 }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#a1a1aa' }} />
                    <Line
                      type="monotone" dataKey="copilot" name="Copilot"
                      stroke="#6366f1" strokeWidth={2} dot={false}
                    />
                    <Line
                      type="monotone" dataKey="audio" name="Audio"
                      stroke="#06b6d4" strokeWidth={2} dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-zinc-600 text-sm">
                  No usage data yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feature Breakdown Pie */}
          <Card className="bg-zinc-900 border-zinc-800" data-testid="feature-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <Zap className="w-4 h-4" /> By Feature
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                        paddingAngle={3} dataKey="value"
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: '#27272a', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }}
                        itemStyle={{ color: '#e4e4e7' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5 mt-2">
                    {pieData.map((item, i) => (
                      <div key={item.name} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-zinc-400">{item.name}</span>
                        </div>
                        <span className="text-zinc-200 font-medium">{String(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-zinc-600 text-sm">
                  No feature data yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row: Top Users + Audio Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top Users */}
          <Card className="bg-zinc-900 border-zinc-800" data-testid="top-users-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <Users className="w-4 h-4" /> Top Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              {top_users.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-800 text-zinc-500">
                        <th className="text-left py-2 pr-3">User</th>
                        <th className="text-right py-2 px-2">Copilot</th>
                        <th className="text-right py-2 px-2">Audio</th>
                        <th className="text-right py-2 pl-2">Est. Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {top_users.map((u) => {
                        const cost = (
                          u.copilot_calls * costs.copilot_rate_per_call +
                          u.audio_calls * costs.audio_rate_per_call
                        ).toFixed(2);
                        return (
                          <tr key={u.user_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                            <td className="py-2 pr-3">
                              <div className="text-zinc-200 font-medium truncate max-w-[180px]">
                                {u.name || u.email}
                              </div>
                              {u.name && (
                                <div className="text-zinc-600 truncate max-w-[180px]">{u.email}</div>
                              )}
                            </td>
                            <td className="text-right py-2 px-2 text-indigo-400 font-medium">{u.copilot_calls}</td>
                            <td className="text-right py-2 px-2 text-cyan-400 font-medium">{u.audio_calls}</td>
                            <td className="text-right py-2 pl-2 text-amber-400">${cost}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="h-32 flex items-center justify-center text-zinc-600 text-sm">
                  No user data yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audio Files + Cost Config */}
          <div className="space-y-4">
            <Card className="bg-zinc-900 border-zinc-800" data-testid="audio-files-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                  <Database className="w-4 h-4" /> Audio Files
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-xl font-bold text-emerald-400">{audio_files.ready}</p>
                    <p className="text-xs text-zinc-500 mt-1">Ready</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-xl font-bold text-amber-400">{audio_files.pending}</p>
                    <p className="text-xs text-zinc-500 mt-1">Pending</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-xl font-bold text-red-400">{audio_files.failed}</p>
                    <p className="text-xs text-zinc-500 mt-1">Failed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800" data-testid="cost-config-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Cost Rates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-500">Copilot (GPT-5.2)</span>
                    <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                      ${costs.copilot_rate_per_call}/call
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-500">Audio TTS (tts-1)</span>
                    <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                      ${costs.audio_rate_per_call}/call
                    </Badge>
                  </div>
                  <p className="text-zinc-600 mt-2">
                    Configure via COPILOT_COST_PER_CALL and AUDIO_COST_PER_CALL env vars
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
